import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/store/auth'
import { useEffectiveClient } from '@/hooks/useAuth'
import { useProfile, initialsOf } from '@/hooks/useProfile'
import { functionError } from '@/hooks/useMix'
import { ROUTES } from '@/components/gg/chrome'
import {
  useYourList, useListStats, useListUpdate, useSubscribers, useImportSubscribers, useAddSubscriber, useRemoveSubscriber,
  useIssues, useSending, parseList, isEmail, listMissing, type Subscriber,
} from '@/hooks/useNewsletter'

/**
 * Your list (Brian's settings-newsletter.html v2, Oct 29: "remove Mailchimp
 * for now and switch to our own; will add Mailchimp later if it's
 * requested"). GoGrov sends each issue itself, so the list lives here and
 * nothing has to be connected.
 *
 * The subscriber count and the open rate once an issue has gone out; the
 * signup link to share (new subscribers confirm by email first); Import (a CSV
 * or a paste, with the required "these people asked for my emails" check);
 * Add one person; Download your list. The sender: from name, the address it
 * sends from (<handle>@ GoGrov's sending domain), reply-to (required; tests go
 * there) and the inbox preview. The mailing address the law prints on every
 * issue (required). Send day and time. Send a test. The pill says what is
 * missing, in order: subscribers, reply-to, mailing address; then Ready to send.
 *
 * Ours, beyond Brian's board: the people on the list (the brief asks for
 * remove), and this month's sends (handoff section 4: the prototype shows the
 * count only, with no price).
 */
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
const TIMES: string[] = []
for (let m = 5 * 60; m <= 21 * 60; m += 30) TIMES.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`)
const ampm = (t: string) => { const [h, m] = t.split(':').map(Number); return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'a.m.' : 'p.m.'}` }
const ZONE: Record<string, string> = {
  'America/Los_Angeles': 'Pacific', 'America/Vancouver': 'Pacific', 'America/Denver': 'Mountain', 'America/Boise': 'Mountain',
  'America/Phoenix': 'Mountain', 'America/Edmonton': 'Mountain', 'America/Regina': 'Central', 'America/Chicago': 'Central',
  'America/Winnipeg': 'Central', 'America/New_York': 'Eastern', 'America/Toronto': 'Eastern', 'America/Detroit': 'Eastern',
}
const n = (v: number | null | undefined) => Number(v ?? 0).toLocaleString('en-US')

const MC = <svg className="nls-ic" viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="M3.5 7l8.5 6 8.5-6" /></svg>
const SHIELD = <svg className="nls-ic" viewBox="0 0 24 24"><path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" /><path d="M9.5 12l1.8 1.8 3.4-3.6" /></svg>
const SEND = <svg viewBox="0 0 24 24"><path d="M4 12l16-8-6 16-2.5-6.5L4 12z" /><path d="M11.5 13.5L20 4" /></svg>
const UP = <svg viewBox="0 0 24 24"><path d="M12 16V4M7 9l5-5 5 5M5 20h14" /></svg>
const DOWN = <svg viewBox="0 0 24 24"><path d="M12 4v12M7 11l5 5 5-5M5 20h14" /></svg>
const PLUS = <svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>
const PEOPLE = <svg viewBox="0 0 24 24"><circle cx="9" cy="8" r="3.2" /><path d="M3.5 19c.6-3.2 2.9-5 5.5-5s4.9 1.8 5.5 5M15.5 5.2a3 3 0 0 1 0 5.6M17.5 14.4c1.6.6 2.7 2.2 3 4.6" /></svg>
const FILE = <svg className="nls-ic" viewBox="0 0 24 24"><path d="M7 3h7l5 5v13H7z" /><path d="M14 3v5h5M10 13h6M10 17h6" /></svg>

type Sheet = 'import' | 'add' | 'people' | null

export function NewsletterSettings() {
  const client = useEffectiveClient()
  const { data: profile } = useProfile()
  const email = useAuthStore((s) => s.user?.email)
  const { data: list, isLoading, error } = useYourList()
  const { data: stats } = useListStats()
  const { data: sending } = useSending()
  const { data: issues } = useIssues()
  const update = useListUpdate()
  const [toast, setToastState] = useState<ReactNode>(null)
  const tRef = useRef(0)
  const say = (t: ReactNode) => { setToastState(t); window.clearTimeout(tRef.current); tRef.current = window.setTimeout(() => setToastState(null), 3200) }
  const [sheet, setSheet] = useState<Sheet>(null)
  const [from, setFrom] = useState<string | null>(null)
  const [reply, setReply] = useState<string | null>(null)
  const [addr, setAddr] = useState<string | null>(null)
  const [replyErr, setReplyErr] = useState<string | null>(null)
  const [testing, setTesting] = useState(false)
  const [wantPeople, setWantPeople] = useState(false)
  const replyRef = useRef<HTMLInputElement>(null)
  const { data: people } = useSubscribers(wantPeople)
  useEffect(() => { setFrom(null); setReply(null); setAddr(null) }, [list?.from_name, list?.reply_to, list?.mailing_address])

  if (isLoading) return <div className="gg-legacy"><span className="gg-spinner" /></div>
  if (error || !list) return <div className="gg-legacy nls"><p className="gg-error">{(error as Error | null)?.message ?? 'Your list could not be opened.'}</p></div>

  const name = client?.display_name ?? ''
  const subs = stats?.subscribed ?? 0
  const fromV = from ?? list.from_name ?? name
  const replyV = reply ?? list.reply_to ?? ''
  const addrV = addr ?? list.mailing_address ?? ''
  // the pill reads what's saved, plus what's being typed
  const need = listMissing({ ...list, reply_to: isEmail(replyV) ? replyV : list.reply_to, mailing_address: addrV || list.mailing_address }, subs)
  const town = (client?.city_state ?? '').split(',')[0] || 'town'
  const zone = ZONE[client?.timezone ?? ''] ?? 'local'
  const ini = initialsOf(profile?.full_name, email).slice(0, 2) || name.slice(0, 1)
  const sendsFrom = `${list.handle}@${sending?.domain || 'news.gogrov.com'}`
  const signup = `${window.location.host}/n/${list.handle}`
  const hintNormal = 'Needed before an issue can send. Readers’ replies and your test issue come here.'

  function saveFrom() {
    const v = fromV.trim() || name
    if (!fromV.trim()) setFrom(name)
    if (v !== (list!.from_name ?? '')) update.mutate({ from_name: v })
  }
  function saveReply() {
    const v = replyV.trim()
    if (v === (list!.reply_to ?? '')) return
    if (v && !isEmail(v)) { setReplyErr('That doesn’t look like an email address. Check it for typos.'); return }
    update.mutate({ reply_to: v }, { onError: (e) => setReplyErr((e as Error).message) })
  }
  function saveAddr() {
    if (addrV.trim() === (list!.mailing_address ?? '')) return
    update.mutate({ mailing_address: addrV.trim() })
  }
  async function sendTest() {
    const v = replyV.trim()
    if (!v || !isEmail(v)) {
      setReplyErr(v ? 'That doesn’t look like an email address. Fix it and the test goes there.' : 'Add your reply-to address first. The test goes there.')
      replyRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' }); replyRef.current?.focus({ preventScroll: true })
      say(v ? 'Check your reply-to address first.' : <>Add a reply-to address first. <b>The test goes there.</b></>)
      return
    }
    if (v !== list!.reply_to) await update.mutateAsync({ reply_to: v }).catch(() => undefined)
    // The test is this week's issue as it stands: the draft laid out in the builder.
    const draft = (issues ?? []).find((i) => i.status === 'draft' || i.status === 'failed')
    if (!draft) { say(<>Lay out this week’s issue first. <b>The test sends it.</b></>); return }
    setTesting(true)
    const { error: err } = await supabase.functions.invoke('newsletter-send', { body: { issue_id: draft.id, test: true } })
    setTesting(false)
    if (err) { say((await functionError(err)).message); return }
    say('Test sent to your reply-to address')
  }
  function download() {
    if (!people) { setWantPeople(true); say('Getting your list ready. Press Download again in a moment.'); return }
    const q = (v: string | null) => `"${String(v ?? '').replace(/"/g, '""')}"`
    const csv = ['email,name,status,source,joined', ...people.map((p) => [q(p.email), q(p.name), p.status, p.source, p.created_at.slice(0, 10)].join(','))].join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
    const a = document.createElement('a')
    a.href = url; a.download = `${list!.handle}-subscribers.csv`; a.click()
    URL.revokeObjectURL(url)
    say(<>Your list downloaded: <b>{n(people.length)} people</b>, as a CSV.</>)
  }

  return (
    <div className="gg-legacy nls">
      <p className="nls-lead">Your newsletter sends from GoGrov, to the list you keep here. Nothing to connect. Sponsors pay you directly; GoGrov takes no cut.</p>
      {sending && !sending.configured && (
        <p className="nls-off">GoGrov’s sending isn’t switched on yet, so tests and issues can’t go out today. Your list, the sender and the address are saved and ready for when it is.</p>
      )}
      <section className="nls-card">
        <div className="nls-acct">
          <div className="nls-mc">{MC}</div>
          <div className="nls-acct__t">
            <div className="nls-acct__nr"><span className="nls-acct__n">Your list</span><span className={`nls-state ${need ? 'is-need' : 'is-ok'}`}>{need || 'Ready to send'}</span></div>
            <div className="nls-acct__w">Kept in GoGrov · yours to download any time</div>
          </div>
        </div>
        <div className="nls-stats">
          <div className="nls-stat"><div className="nls-stat__k">Subscribers</div><div className="nls-stat__v">{n(subs)}</div></div>
          <div className="nls-stat"><div className="nls-stat__k">Open rate</div>{stats?.open_rate != null ? <div className="nls-stat__v">{stats.open_rate}%</div> : <div className="nls-stat__v is-soft">Shows after your first issue goes out</div>}</div>
        </div>
        {!!(stats?.pending || stats?.unsubscribed || stats?.bounced) && (
          <p className="nls-fine">
            {[stats.pending ? `${n(stats.pending)} waiting to confirm` : '', stats.unsubscribed ? `${n(stats.unsubscribed)} unsubscribed` : '', stats.bounced ? `${n(stats.bounced)} bounced` : ''].filter(Boolean).join(' · ')}
          </p>
        )}
        <div className="nls-link"><code>{signup}</code>
          <button type="button" className="gg-btn gg-btn--sm" onClick={() => { try { void navigator.clipboard.writeText(`${window.location.protocol}//${signup}`) } catch { /* no clipboard */ } say(<>Signup link copied. <b>Share it anywhere.</b></>) }}>Copy</button>
        </div>
        <p className="nls-fine">Your signup page. Share it anywhere; new subscribers confirm by email before their first issue. <Link to={`/n/${list.handle}`} target="_blank">Open it</Link></p>
        <div className="nls-acts">
          <button type="button" className="gg-btn nls-gl" onClick={() => setSheet('import')}>{UP}Import</button>
          <button type="button" className="gg-btn nls-gl" onClick={() => setSheet('add')}>{PLUS}Add one person</button>
          <button type="button" className="gg-btn nls-gl" onClick={() => { setWantPeople(true); setSheet('people') }}>{PEOPLE}People on your list</button>
          <button type="button" className="gg-btn nls-gl" disabled={!subs && !stats?.pending} onClick={download} onMouseEnter={() => setWantPeople(true)} onFocus={() => setWantPeople(true)}>{DOWN}Download your list (CSV)</button>
        </div>
        <p className="nls-fine">Moving from Mailchimp or another service? Export your list there as a CSV and import it here.</p>
      </section>

      <div className="nls-sect">Sender</div>
      <section className="nls-card">
        <div className="nls-fld">
          <label htmlFor="nls-from">From name</label>
          <input id="nls-from" className="nls-in" type="text" autoComplete="off" value={fromV} placeholder={name}
            onChange={(e) => setFrom(e.target.value)} onBlur={saveFrom} />
          <div className="nls-hint">Who the issue is from in your readers’ inbox.</div>
        </div>
        <div className="nls-fld">
          <label>Sends from</label>
          <div className="nls-in nls-in--ro">{sendsFrom}</div>
          <div className="nls-hint">GoGrov’s sending address, set up for you. Your own domain comes later.</div>
        </div>
        <div className="nls-fld">
          <label htmlFor="nls-reply">Reply-to address <em>required</em></label>
          <input id="nls-reply" ref={replyRef} className={`nls-in${replyErr ? ' is-bad' : ''}`} type="email" inputMode="email" autoComplete="email"
            value={replyV} placeholder="name@yourdomain.com" onChange={(e) => { setReply(e.target.value); setReplyErr(null) }} onBlur={saveReply} />
          <div className={`nls-hint${replyErr ? ' is-err' : ''}`}>{replyErr ?? hintNormal}</div>
        </div>
        <div className="nls-inbox" aria-label="How it shows in the inbox">
          <span className="nls-inbox__av" style={{ background: client?.accent_color || 'var(--amber-deep)' }}>{ini}</span>
          <div className="nls-inbox__t">
            <div className="nls-inbox__k">In the inbox</div>
            <div className="nls-inbox__fr"><span>{fromV.trim() || name}</span><span>{list.send_day.slice(0, 3)}</span></div>
            <div className="nls-inbox__sj">This week in {town}</div>
            <div className="nls-inbox__rt">{replyV.trim() ? `Replies go to ${replyV.trim()}` : 'Replies go to your reply-to address'}</div>
          </div>
        </div>
      </section>

      <div className="nls-sect">Mailing address</div>
      <section className="nls-card">
        <div className="nls-fld">
          <label htmlFor="nls-addr">Printed at the foot of every issue <em>required</em></label>
          <input id="nls-addr" className="nls-in" type="text" autoComplete="street-address" value={addrV} placeholder="Business address or PO box"
            onChange={(e) => setAddr(e.target.value)} onBlur={saveAddr} />
          <div className="nls-hint">The law asks every newsletter to carry one. It doesn’t have to be your home: a business address or a PO box works.</div>
        </div>
      </section>

      <div className="nls-sect">Schedule</div>
      <section className="nls-card">
        <div className="nls-two">
          <div className="nls-fld">
            <label htmlFor="nls-day">Send day</label>
            <select id="nls-day" className="nls-in" value={list.send_day} onChange={(e) => update.mutate({ send_day: e.target.value })}>
              {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="nls-fld">
            <label htmlFor="nls-time">Time</label>
            <select id="nls-time" className="nls-in" value={list.send_time} onChange={(e) => update.mutate({ send_time: e.target.value })}>
              {TIMES.map((t) => <option key={t} value={t}>{ampm(t)}</option>)}
            </select>
          </div>
        </div>
        <p className="nls-fine">New issues are set for <b>{list.send_day} at {ampm(list.send_time)}</b>, {zone === 'local' ? 'your time' : `${zone} time`}. You review each issue before it goes.</p>
      </section>

      <button type="button" className="gg-btn nls-gl" disabled={testing} onClick={() => void sendTest()}>{SEND}{testing ? 'Sending' : 'Send a test to me'}</button>
      <div className="nls-law">{SHIELD}<span><b>GoGrov adds the unsubscribe link and your mailing address</b> to the foot of every issue, as the law requires (CAN-SPAM). People who unsubscribe, or whose address bounces, leave your list on their own.</span></div>
      {!!stats?.month_emails && (
        <p className="nls-fine nls-usage">This month: {n(stats.month_issues)} {stats.month_issues === 1 ? 'issue' : 'issues'} · {n(stats.month_emails)} emails sent.</p>
      )}
      <p className="nls-fine"><Link to={ROUTES.newsletter}>This week’s issue →</Link></p>

      {sheet && (
        <div className="nls-scrim" onClick={(e) => { if (e.target === e.currentTarget) setSheet(null) }}>
          <div className="nls-sheet" role="dialog" aria-modal="true" aria-labelledby="nls-sheet-t">
            <div className="nls-grab" />
            {sheet === 'import' && <ImportSheet onClose={() => setSheet(null)} say={say} />}
            {sheet === 'add' && <AddSheet onClose={() => setSheet(null)} say={say} />}
            {sheet === 'people' && <PeopleSheet people={people} onClose={() => setSheet(null)} say={say} />}
          </div>
        </div>
      )}
      {toast && <div className="nls-toast" role="status">{toast}</div>}
    </div>
  )
}

/** Import: a file or a paste; nothing comes in until they confirm these people asked for their emails. */
function ImportSheet({ onClose, say }: { onClose: () => void; say: (t: ReactNode) => void }) {
  const imp = useImportSubscribers()
  const [text, setText] = useState('')
  const [fname, setFname] = useState<string | null>(null)
  const [agree, setAgree] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const rows = useMemo(() => parseList(text), [text])
  async function readFile(f: File | undefined) {
    if (!f) return
    if (f.size > 5 * 1024 * 1024) { setErr('That file is over 5 MB. Split it into smaller files.'); return }
    setText(await f.text()); setFname(f.name); setErr(null)
  }
  async function go() {
    setErr(null)
    try {
      const r = await imp.mutateAsync({ rows, consent: agree })
      onClose()
      const skipped = [r.duplicates ? `${n(r.duplicates)} already on your list` : '', r.role ? `${n(r.role)} shared addresses like info@` : '', r.invalid ? `${n(r.invalid)} not email addresses` : ''].filter(Boolean)
      say(<>{n(r.added)} imported.{skipped.length ? ` Skipped ${skipped.join(', ')}.` : ''}</>)
    } catch (e) { setErr((e as Error).message) }
  }
  return (
    <>
      <h3 id="nls-sheet-t">Import subscribers</h3>
      <p className="nls-sub">A CSV from Mailchimp, Substack, a spreadsheet or a signup sheet. GoGrov reads the email column and skips duplicates.</p>
      {fname ? (
        <div className="nls-file">{FILE}<div><b>{fname}</b><span>{n(rows.length)} email addresses</span></div>
          <button type="button" className="gg-btn gg-btn--sm" onClick={() => { setText(''); setFname(null) }}>Change</button></div>
      ) : (
        <>
          <label className="gg-btn nls-gl nls-pick">{UP}Choose a CSV file<input type="file" accept=".csv,.txt,text/csv,text/plain" hidden onChange={(e) => void readFile(e.target.files?.[0])} /></label>
          <div className="nls-fld">
            <label htmlFor="nls-paste">Or paste addresses <em>one a line, or a CSV with an email column</em></label>
            <textarea id="nls-paste" className="nls-in nls-ta" value={text} onChange={(e) => setText(e.target.value)} placeholder={`email,name\nsam@example.com,Sam Lee`} />
            {!!text.trim() && <div className="nls-hint">{n(rows.length)} email {rows.length === 1 ? 'address' : 'addresses'} found</div>}
          </div>
        </>
      )}
      <label className="nls-consent"><input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
        <span><b>These people asked to get my emails.</b> No bought, scraped or borrowed lists: they hurt every GoGrov sender’s delivery and break the law.</span></label>
      {err && <p className="gg-error">{err}</p>}
      <div className="nls-btns">
        <button type="button" className="gg-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="gg-btn gg-btn--amber" disabled={!agree || !rows.length || imp.isPending} onClick={() => void go()}>
          {imp.isPending ? 'Importing' : `Import ${n(rows.length)}`}
        </button>
      </div>
    </>
  )
}

function AddSheet({ onClose, say }: { onClose: () => void; say: (t: ReactNode) => void }) {
  const add = useAddSubscriber()
  const [v, setV] = useState('')
  const [err, setErr] = useState<string | null>(null)
  async function go() {
    const e = v.trim()
    if (!isEmail(e)) { setErr('That doesn’t look like an email address.'); return }
    setErr(null)
    try {
      const r = await add.mutateAsync({ email: e })
      onClose()
      say(r.already ? <>{e} is already on your list.</> : <>Confirm email sent to {e}. <b>They join your list when they confirm.</b></>)
    } catch (x) { setErr((x as Error).message) }
  }
  return (
    <>
      <h3 id="nls-sheet-t">Add one person</h3>
      <p className="nls-sub">Someone who asked you in person or by message. They get an email to confirm before their first issue.</p>
      <div className="nls-fld">
        <input className={`nls-in${err ? ' is-bad' : ''}`} type="email" inputMode="email" autoComplete="off" placeholder="their@email.com" value={v}
          onChange={(e) => { setV(e.target.value); setErr(null) }} onKeyDown={(e) => { if (e.key === 'Enter') void go() }} />
        {err && <div className="nls-hint is-err">{err}</div>}
      </div>
      <div className="nls-btns">
        <button type="button" className="gg-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="gg-btn gg-btn--amber" disabled={add.isPending} onClick={() => void go()}>{add.isPending ? 'Sending' : 'Send them the confirm email'}</button>
      </div>
    </>
  )
}

const STATUS: Record<Subscriber['status'], string> = {
  subscribed: 'On the list', pending: 'Waiting to confirm', unsubscribed: 'Unsubscribed', bounced: 'Bounced', complained: 'Marked as spam',
}

/** Who is on the list, with Remove (ours: Brian's board shows the count only). */
function PeopleSheet({ people, onClose, say }: { people: Subscriber[] | undefined; onClose: () => void; say: (t: ReactNode) => void }) {
  const remove = useRemoveSubscriber()
  const [q, setQ] = useState('')
  const shown = (people ?? []).filter((p) => !q.trim() || p.email.includes(q.trim().toLowerCase()) || (p.name ?? '').toLowerCase().includes(q.trim().toLowerCase()))
  return (
    <>
      <h3 id="nls-sheet-t">People on your list</h3>
      <p className="nls-sub">People who unsubscribed, bounced or marked an issue as spam stay here, so an import can never add them back.</p>
      <input className="nls-in" type="search" placeholder="Find someone" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="nls-people">
        {!people ? <span className="gg-spinner" /> : !shown.length ? <p className="nls-fine">{people.length ? 'Nobody matches.' : 'Nobody yet. Import your list or share your signup link.'}</p> : shown.slice(0, 300).map((p) => (
          <div key={p.id} className="nls-person">
            <span className="nls-person__t"><b>{p.email}</b><span>{[p.name, STATUS[p.status], p.source === 'import' ? 'imported' : p.source === 'signup' ? 'signed up' : 'added by you'].filter(Boolean).join(' · ')}</span></span>
            {(p.status === 'subscribed' || p.status === 'pending') && (
              <button type="button" className="gg-btn gg-btn--sm" disabled={remove.isPending}
                onClick={() => remove.mutate(p.id, { onSuccess: () => say(<>{p.email} is off your list.</>), onError: (e) => say((e as Error).message) })}>Remove</button>
            )}
          </div>
        ))}
        {shown.length > 300 && <p className="nls-fine">Showing 300 of {n(shown.length)}. Search to find someone.</p>}
      </div>
      <div className="nls-btns"><button type="button" className="gg-btn" onClick={onClose}>Done</button></div>
    </>
  )
}
